/**
 * Renders the project CHANGELOG.md (served from /CHANGELOG.md by nginx /
 * the dev server). Parsing lives in utils/changelogParser.ts (pure, unit
 * tested against the real file); this component only maps the parsed blocks
 * to Ant Design elements.
 *
 * Each `## [version]` heading becomes its own collapsible panel (most recent
 * expanded by default) instead of one long scroll of every release ever
 * shipped.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Spin, Empty, Typography, Tag, Collapse } from 'antd';
import type { CollapseProps } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import AppPageLayout from './common/AppPageLayout';
import PageHeader from './common/PageHeader';
import {
  Block,
  ChangelogSection,
  Inline,
  looksLikeChangelog,
  parseMarkdown,
  splitChangelog,
} from '../utils/changelogParser';

const { Title, Paragraph, Text } = Typography;

const APP_VERSION = process.env.REACT_APP_VERSION || 'dev';

// ── Parsed markdown → React ────────────────────────────────────────────────

function renderInline(nodes: Inline[], keyBase: string): React.ReactNode[] {
  return nodes.map((n, i) => {
    const key = `${keyBase}-${i}`;
    switch (n.type) {
      case 'text':
        return <React.Fragment key={key}>{n.text}</React.Fragment>;
      case 'bold':
        return <strong key={key}>{renderInline(n.children, key)}</strong>;
      case 'italic':
        return <em key={key}>{renderInline(n.children, key)}</em>;
      case 'code':
        return <Text code key={key}>{n.text}</Text>;
      case 'link':
        return (
          <a key={key} href={n.href} target="_blank" rel="noopener noreferrer">
            {renderInline(n.children, key)}
          </a>
        );
    }
  });
}

function cellStyle(header: boolean): React.CSSProperties {
  return {
    border: '1px solid #e8e8e8',
    padding: '6px 10px',
    textAlign: 'left',
    verticalAlign: 'top',
    background: header ? 'rgba(0,0,0,0.03)' : undefined,
    fontWeight: header ? 600 : undefined,
  };
}

/** A list item's leading paragraph renders inline (no extra <p> margin). */
function renderListItem(blocks: Block[], keyBase: string): React.ReactNode[] {
  return blocks.map((b, i) => {
    const key = `${keyBase}-${i}`;
    if (i === 0 && b.type === 'paragraph') {
      return <React.Fragment key={key}>{renderInline(b.inline, key)}</React.Fragment>;
    }
    return <React.Fragment key={key}>{renderBlocks([b], key)}</React.Fragment>;
  });
}

function renderBlocks(blocks: Block[], keyBase: string): React.ReactNode[] {
  return blocks.map((b, i) => {
    const key = `${keyBase}-${i}`;
    switch (b.type) {
      case 'heading': {
        // `##` never reaches here (consumed as section headings): # → h2, ### → h4, #### → h5.
        const level = (b.level === 1 ? 2 : Math.min(b.level + 1, 5)) as 2 | 3 | 4 | 5;
        const marginTop = level === 2 ? 0 : level === 3 ? 24 : level === 4 ? 18 : 14;
        return (
          <Title level={level} key={key} style={{ marginTop }}>
            {renderInline(b.inline, key)}
          </Title>
        );
      }
      case 'paragraph':
        return <Paragraph key={key}>{renderInline(b.inline, key)}</Paragraph>;
      case 'list': {
        const ListTag = b.ordered ? 'ol' : 'ul';
        return (
          <ListTag key={key} style={{ paddingLeft: 24, marginBottom: 16 }}>
            {b.items.map((item, j) => (
              <li key={j} style={{ marginBottom: 6 }}>
                {renderListItem(item.blocks, `${key}-${j}`)}
              </li>
            ))}
          </ListTag>
        );
      }
      case 'code':
        return (
          <pre
            key={key}
            style={{
              background: 'rgba(0,0,0,0.04)',
              padding: 12,
              borderRadius: 6,
              overflowX: 'auto',
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            <code>{b.text}</code>
          </pre>
        );
      case 'table':
        return (
          <div key={key} style={{ overflowX: 'auto', marginBottom: 16 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 14 }}>
              <thead>
                <tr>
                  {b.header.map((cell, c) => (
                    <th key={c} style={cellStyle(true)}>{renderInline(cell, `${key}-h${c}`)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((row, r) => (
                  <tr key={r}>
                    {row.map((cell, c) => (
                      <td key={c} style={cellStyle(false)}>{renderInline(cell, `${key}-${r}-${c}`)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'quote':
        return (
          <blockquote
            key={key}
            style={{ margin: '0 0 16px', padding: '4px 16px', borderLeft: '3px solid #d9d9d9', color: '#595959' }}
          >
            {renderBlocks(b.blocks, key)}
          </blockquote>
        );
    }
  });
}

function sectionLabel(section: ChangelogSection): React.ReactNode {
  const { heading } = section;
  if (!heading.matched) {
    // Not "[version] — date": show the raw heading text.
    return <Text strong style={{ fontSize: 15 }}>{heading.raw}</Text>;
  }
  return (
    <span style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
      <Tag color={heading.isUnreleased ? 'orange' : 'blue'} style={{ fontSize: 14, padding: '2px 10px', fontWeight: 600 }}>
        {heading.isUnreleased ? 'Unreleased' : `v${heading.version}`}
      </Tag>
      {heading.date && <Text type="secondary">{heading.date}</Text>}
    </span>
  );
}

// ── Component ──────────────────────────────────────────────────────────────

const ChangelogPage: React.FC = () => {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch('/CHANGELOG.md', { cache: 'no-cache' })
      .then((res) => (res.ok ? res.text() : Promise.reject(new Error(`HTTP ${res.status}`))))
      // A host that falls back to index.html (200) for a missing file must
      // show the error state, not the page's HTML parsed as markdown.
      .then((text) => { if (!cancelled) setContent(looksLikeChangelog(text) ? text : ''); })
      .catch(() => { if (!cancelled) setContent(''); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const { intro, sections } = useMemo(
    () => (content ? splitChangelog(content) : { intro: '', sections: [] as ChangelogSection[] }),
    [content]
  );

  const collapseItems: CollapseProps['items'] = useMemo(
    () =>
      sections.map((s, i) => ({
        key: `sec-${i}`,
        label: sectionLabel(s),
        children: renderBlocks(parseMarkdown(s.body), `sec-${i}`),
      })),
    [sections]
  );

  return (
    <AppPageLayout maxWidth={820}>
      <PageHeader
        icon={<HistoryOutlined />}
        title="Changelog"
        subtitle={`Histórico de versiones desplegadas. Versión actual: v${APP_VERSION}`}
      />

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
          <Spin size="large" />
        </div>
      ) : !content ? (
        <Empty description="No se pudo cargar el changelog" />
      ) : (
        <>
          {intro.trim() && (
            <div style={{ background: '#fff', padding: 24, borderRadius: 8, marginBottom: 16 }}>
              {renderBlocks(parseMarkdown(intro), 'intro')}
            </div>
          )}
          <Collapse
            items={collapseItems}
            defaultActiveKey={sections[0] ? ['sec-0'] : []}
          />
        </>
      )}
    </AppPageLayout>
  );
};

export default ChangelogPage;
